document.addEventListener("DOMContentLoaded", function () {
  // ==============================================
  // 1) IMMEDIATE SLIDER INITIALIZATION
  // ==============================================
  const initLeaseSlider = () => {
    const slider = document.getElementById("leaseAmountSlider");
    const valueDisplay = document.getElementById("leaseAmountValue");
    const businessTypeRadios = document.querySelectorAll('input[name="business_type"]');

    if (!slider || !valueDisplay || !businessTypeRadios.length) return;

    // Update slider appearance + label
    const updateSlider = () => {
      const value = parseInt(slider.value, 10);
      const min = parseInt(slider.min || 0, 10);
      const max = parseInt(slider.max || 0, 10);
      const percent = ((value - min) / (max - min)) * 100;

      // CAD display "C$ 50,000"
      valueDisplay.textContent =
        "$ " +
        new Intl.NumberFormat("en-CA", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);

      slider.style.background = `
        linear-gradient(to right, 
          #23a6d5 0%, 
          #05559a ${percent}%, 
          #dee2e6 ${percent}%, 
          #dee2e6 100%)
      `;
    };

    // Adjust min based on business type
    const updateMinValue = () => {
      const isStartup =
        document.querySelector('input[name="business_type"]:checked')?.value === "startup";
      slider.min = isStartup ? 10000 : 3000;
      slider.value = 50000; // reset to a sensible mid value
      updateSlider();
    };

    // Initialize
    updateSlider();
    updateMinValue();

    // Events
    slider.addEventListener("input", updateSlider);
    businessTypeRadios.forEach((radio) => {
      radio.addEventListener("change", updateMinValue);
    });
  };

  // ==============================================
  // 2) ANIMATED PROCESS TIMELINE
  // ==============================================
  const initProcessAnimation = () => {
    const processSection = document.querySelector(".four-step-process");
    if (!processSection) return;

    const processSteps = document.querySelectorAll(".process-step");
    const config = {
      initialDelay: 500,
      stepDelay: window.innerWidth < 768 ? 800 : 1500,
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        processSection.classList.add("is-in-view");
        processSteps.forEach((step, index) => {
          setTimeout(() => {
            step.classList.add("is-active");
            setTimeout(() => step.classList.add("is-complete"), config.stepDelay);
          }, config.initialDelay + index * config.stepDelay);
        });
        observer.disconnect();
      }
    }, config);

    observer.observe(processSection);
  };

  // ==============================================
  // 3) TABBED CONTENT
  // ==============================================
  const initTabs = () => {
    const tabButtons = document.querySelectorAll(".tab-button");
    const tabContents = document.querySelectorAll(".tab-content");

    if (!tabButtons.length || !tabContents.length) return;

    if (!document.querySelector(".tab-button.active")) {
      tabButtons[0].classList.add("active");
      tabContents[0].classList.add("active");
    }

    tabButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const targetId = button.getAttribute("data-tab-target");
        const targetContent = document.querySelector(`[data-tab-content="${targetId}"]`);

        tabButtons.forEach((btn) => btn.classList.remove("active"));
        tabContents.forEach((content) => content.classList.remove("active"));

        button.classList.add("active");
        if (targetContent) targetContent.classList.add("active");
      });
    });
  };

  // ==============================================
  // 4) ANIMATED GRADIENT BACKGROUND
  // ==============================================
  const initGradient = () => {
    const gradient = document.querySelector(".hero-gradient-bg");
    if (!gradient) return;

    let frameId = null;
    let hue = 0;

    const updateGradient = () => {
      hue = (hue + 0.3) % 360;
      const time = performance.now() * 0.0005;
      const x = 70 + Math.sin(time) * 15;
      const y = 30 + Math.cos(time * 1.4) * 15;

      gradient.style.background = `
        radial-gradient(ellipse at ${x}% ${y}%, hsla(${(200 + hue) % 360}, 100%, 70%, 0.6), transparent 70%),
        radial-gradient(ellipse at ${100 - x}% ${100 - y}%, hsla(${(330 + hue) % 360}, 100%, 70%, 0.6), transparent 70%)
      `;

      frameId = requestAnimationFrame(updateGradient);
    };

    frameId = requestAnimationFrame(updateGradient);
  };

  // ==============================================
  // 5) PAYMENT CALCULATOR (button + dynamic note)
  // ==============================================
  const initPaymentCalculator = () => {
    const FACTORS = {
      established: {
        24: 0.0508957947358593,
        36: 0.0357828885285011,
        48: 0.0282731489798935,
        60: 0.0238043535150799,
      },
      startup: {
        24: 0.0536267778084269,
        36: 0.0386701728309803,
      },
    };

    const slider = document.getElementById("leaseAmountSlider");
    const termSelect = document.querySelector(".dashboard-panel select.custom-select");
    const businessTypeRadios = document.querySelectorAll('input[name="business_type"]');
    const calcBtn = document.querySelector(".btn-calculate-payments");
    const note = document.querySelector(".payment-note");

    if (!slider || !termSelect || !businessTypeRadios.length || !calcBtn || !note) return;

    const getBusinessType = () =>
      document.querySelector('input[name="business_type"]:checked')?.value === "startup"
        ? "startup"
        : "established";

    const getSelectedTerm = () => parseInt(termSelect.value, 10);

    const formatCAD = (num) =>
      "$" +
      new Intl.NumberFormat("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);

    // Show only terms supported by the selected business type
    const syncTermOptionsForType = () => {
      const type = getBusinessType();
      const supported = new Set(Object.keys(FACTORS[type]).map(Number));

      Array.from(termSelect.options).forEach((opt) => {
        const months = parseInt(opt.value, 10);
        const ok = supported.has(months);
        opt.disabled = !ok;
        opt.hidden = !ok;
      });

      if (!supported.has(getSelectedTerm())) {
        const first = Array.from(termSelect.options).find((o) => !o.disabled);
        if (first) termSelect.value = first.value;
      }
    };

    const calculatePayment = () => {
      const amount = parseFloat(slider.value || 0);
      const term = getSelectedTerm();
      const type = getBusinessType();
      const factor = FACTORS[type]?.[term];

      if (!factor) {
        note.textContent = "Payment unavailable for the selected term.";
        return;
      }

      const monthly = amount * factor;
      note.textContent = `Your monthly payment: ${formatCAD(monthly)} plus sales tax`;
    };

    // Events
    calcBtn.addEventListener("click", calculatePayment);
    businessTypeRadios.forEach((r) =>
      r.addEventListener("change", () => {
        syncTermOptionsForType();
      })
    );

    // Initialize term visibility once
    syncTermOptionsForType();
  };

  // ==============================================
  // Initialize everything
  // ==============================================
  initLeaseSlider();
  initProcessAnimation();
  initTabs();
  initGradient();
  initPaymentCalculator();
});
